import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuthStore } from '../../store/authStore';
import glassTheme, { glassMixins } from '../../styles/glassTheme';
import toast from 'react-hot-toast';
import { TermsAgreement } from './TermsAgreement';
import { TermsAcceptedData } from '../../services/authApi';

const FormContainer = styled.div`
  ${glassTheme.glass.heavy}
  padding: 2rem;
  width: 90%;
  max-width: 420px;
  max-height: 100vh;
  overflow-y: auto;
  box-shadow: ${glassTheme.shadow.xl}, ${glassTheme.shadow.glow};

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${glassTheme.colors.accent.blue};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${glassTheme.colors.accent.purple};
  }
`;

const FormTitle = styled.h2`
  text-align: center;
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
  background: ${glassTheme.gradients.cyber};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.35rem;
  color: ${glassTheme.colors.text.primary};
  font-weight: 500;
  font-size: 0.875rem;
`;

const Input = styled.input`
  ${glassMixins.input}
  width: 100%;
  padding: 0.75rem;
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: ${glassTheme.colors.accent.blue};
    background: rgba(0, 0, 0, 0.7);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
  }

  &::placeholder {
    color: ${glassTheme.colors.text.muted};
    font-size: 0.875rem;
  }
`;

interface PasswordValidationProps {
  $valid: boolean;
  children: React.ReactNode;
}

const PasswordValidation = styled.div<PasswordValidationProps>`
  margin-top: 0.35rem;
  font-size: 0.75rem;
  color: ${props => props.$valid ? glassTheme.colors.status.success : glassTheme.colors.status.error};
`;

const Button = styled.button`
  ${glassMixins.button}
  width: 100%;
  padding: 0.75rem 1.5rem;
  margin-bottom: 0.75rem;
  font-size: 0.9rem;

  &:hover {
    background: ${glassTheme.gradients.cyber};
    box-shadow: ${glassTheme.shadow.xl}, ${glassTheme.shadow.glow};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMessage = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible',
})<{ visible: boolean }>`
  color: ${glassTheme.colors.status.error};
  font-size: 0.9rem;
  margin-top: 0.5rem;
  text-align: center;
  display: ${props => props.visible ? 'block' : 'none'};
`;

const FormFooter = styled.div`
  text-align: center;
  color: ${glassTheme.colors.text.secondary};
  margin-top: 1rem;
  font-size: 0.875rem;

  a {
    color: ${glassTheme.colors.accent.blue};
    text-decoration: none;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const Spinner = styled.div`
  border: 2px solid ${glassTheme.colors.border};
  border-radius: 50%;
  border-top: 2px solid ${glassTheme.colors.accent.blue};
  width: 16px;
  height: 16px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const RoleSelector = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`;

const RoleButton = styled.button<{ $selected: boolean }>`
  flex: 1;
  padding: 0.5rem;
  background: ${props => props.$selected
    ? glassTheme.gradients.cyber
    : 'rgba(0, 0, 0, 0.3)'};
  border: 2px solid ${props => props.$selected
    ? 'transparent'
    : glassTheme.colors.border};
  border-radius: ${glassTheme.borderRadius.md};
  color: ${glassTheme.colors.text.primary};
  cursor: pointer;
  transition: all ${glassTheme.transition.base};
  font-weight: 600;
  font-size: 0.75rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;

  &:hover {
    border-color: ${glassTheme.colors.accent.blue};
  }

  i {
    font-size: 1rem;
  }
`;

interface RegisterFormProps {
  onSwitchToLogin: () => void;
  onClose: () => void;
}

export const RegisterForm: React.FC<RegisterFormProps> = ({ onSwitchToLogin, onClose }) => {
  const navigate = useNavigate();
  const [termsAccepted, setTermsAccepted] = useState<TermsAcceptedData | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [role, setRole] = useState<'student' | 'teacher'>('student');
  const [courseCode, setCourseCode] = useState('');
  const { register, isLoading, error, clearError } = useAuthStore();

  // 验证密码
  useEffect(() => {
    if (confirmPassword && password !== confirmPassword) {
      setPasswordError('两次输入的密码不一致');
    } else if (password && password.length < 6) {
      setPasswordError('密码长度至少为6位');
    } else {
      setPasswordError('');
    }
  }, [password, confirmPassword]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (passwordError) {
      return;
    }

    if (role === 'student' && !courseCode.trim()) {
      toast.error('请输入课程代码');
      return;
    }

    const registerData: any = {
      name,
      email,
      password,
      role,
      termsAccepted
    };

    if (role === 'student') {
      registerData.courseCode = courseCode;
    }

    const success = await register(name, email, password, registerData);
    if (success) {
      // Registration successful
      onClose();
      if (role === 'teacher') {
        toast.success('注册成功！请等待管理员审核您的教师账号');
        navigate('/dashboard');
      } else {
        navigate('/user-info');
      }
    }
  };

  const handleInputChange = (setter: React.Dispatch<React.SetStateAction<string>>) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      setter(e.target.value);
      if (error) clearError();
    };
  };

  const isFormValid = (() => {
    const baseValid = name && email && password && confirmPassword && !passwordError && password.length >= 6;
    if (role === 'student') {
      return baseValid && courseCode.trim();
    }
    return baseValid;
  })();

  // 如果未同意条款，显示协议页面
  if (!termsAccepted) {
    return (
      <TermsAgreement
        onAgree={(data) => setTermsAccepted(data)}
        onCancel={onSwitchToLogin}
      />
    );
  }

  return (
    <FormContainer>
      <FormTitle>创建账户</FormTitle>
      <form onSubmit={handleSubmit}>
        <FormGroup>
          <Label>选择角色</Label>
          <RoleSelector>
            <RoleButton
              type="button"
              $selected={role === 'student'}
              onClick={() => setRole('student')}
            >
              <i>👨‍🎓</i>
              <span>学生</span>
            </RoleButton>
            <RoleButton
              type="button"
              $selected={role === 'teacher'}
              onClick={() => setRole('teacher')}
            >
              <i>👨‍🏫</i>
              <span>教师</span>
            </RoleButton>
          </RoleSelector>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="name">用户名</Label>
          <Input
            id="name"
            type="text"
            placeholder="请输入用户名"
            value={name}
            onChange={handleInputChange(setName)}
            required
            disabled={isLoading}
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="email">邮箱地址</Label>
          <Input
            id="email"
            type="email"
            placeholder="请输入邮箱地址"
            value={email}
            onChange={handleInputChange(setEmail)}
            required
            disabled={isLoading}
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="password">密码</Label>
          <Input
            id="password"
            type="password"
            placeholder="请输入密码（至少6位）"
            value={password}
            onChange={handleInputChange(setPassword)}
            required
            disabled={isLoading}
            minLength={6}
          />
          <PasswordValidation $valid={password.length >= 6}>
            {password.length >= 6 ? '✓ 密码强度符合要求' : '密码长度至少为6位'}
          </PasswordValidation>
        </FormGroup>
        <FormGroup>
          <Label htmlFor="confirmPassword">确认密码</Label>
          <Input
            id="confirmPassword"
            type="password"
            placeholder="请再次输入密码"
            value={confirmPassword}
            onChange={handleInputChange(setConfirmPassword)}
            required
            disabled={isLoading}
          />
          {confirmPassword && (
            <PasswordValidation $valid={password === confirmPassword}>
              {password === confirmPassword ? '✓ 密码匹配' : '两次输入的密码不一致'}
            </PasswordValidation>
          )}
        </FormGroup>

        {role === 'student' && (
          <FormGroup>
            <Label htmlFor="courseCode">课程代码 *</Label>
            <Input
              id="courseCode"
              type="text"
              placeholder="请输入教师提供的课程代码"
              value={courseCode}
              onChange={handleInputChange(setCourseCode)}
              required={role === 'student'}
              disabled={isLoading}
              style={{ textTransform: 'uppercase', fontFamily: 'monospace' }}
            />
          </FormGroup>
        )}
        <ErrorMessage visible={!!error}>
          {error}
        </ErrorMessage>
        <Button type="submit" disabled={!isFormValid || isLoading}>
          {isLoading ? (
            <>
              <Spinner />
              注册中...
            </>
          ) : (
            <>
              <i className="fas fa-user-plus"></i>
              注册
            </>
          )}
        </Button>
      </form>
      <FormFooter>
        <button type="button" onClick={() => setTermsAccepted(null)} style={{
          background: 'none',
          border: 'none',
          color: glassTheme.colors.text.muted,
          textDecoration: 'none',
          cursor: 'pointer',
          padding: 0,
          font: 'inherit',
          fontSize: '0.8rem',
          marginBottom: '0.5rem',
          display: 'block'
        }}>
          <i className="fas fa-arrow-left" style={{ marginRight: '0.25rem' }}></i>
          返回查看服务条款
        </button>
        已有账户？<button type="button" onClick={onSwitchToLogin} style={{
          background: 'none',
          border: 'none',
          color: glassTheme.colors.accent.blue,
          textDecoration: 'underline',
          cursor: 'pointer',
          padding: 0,
          font: 'inherit'
        }}>立即登录</button>
      </FormFooter>
    </FormContainer>
  );
};